/**
 * /companies/review (firm admins): companies proposed from the employer on
 * existing cases. Nothing links until the admin approves; merging joins near
 * matches into one company.
 */
import { ArrowLeft, GitMerge, Split } from "lucide-react";
import { useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { useFirmPermissions } from "@/hooks/useFirmPermissions";
import { CompanyMigrationAPI } from "../api/companiesApi";
import {
  decisionsFor,
  emptyReview,
  merge,
  proposedCompanies,
  rootOf,
  split,
  type ReviewState
} from "../migrationModel";
import {
  refreshCompanies,
  useCompanies,
  useMigrationProposals
} from "../queries";
import { COMPANIES_PATH } from "../routes";
import { formatAddress } from "../utils";

export const MigrationReviewPage: FC = () => {
  const permissions = useFirmPermissions();
  const isAdmin = permissions.can("manage_firm_settings");
  const proposals = useMigrationProposals(isAdmin);
  const companies = useCompanies();
  const [state, setState] = useState<ReviewState>(emptyReview);
  const [busy, setBusy] = useState(false);

  const groups = useMemo(() => proposals.data?.groups ?? [], [proposals.data]);
  const proposed = useMemo(
    () => proposedCompanies(groups, state),
    [groups, state]
  );
  const near = (proposals.data?.near ?? []).filter(
    (n) => rootOf(state, n.group_ids[0]) !== rootOf(state, n.group_ids[1])
  );
  const decisions = decisionsFor(groups, state);
  const existingName = (id: string) =>
    companies.data?.find((c) => c.id === id)?.name ?? "an existing company";

  if (permissions.isLoading || proposals.isLoading)
    return <Skeleton className="m-4 h-40" />;
  if (!isAdmin) {
    return (
      <div className="p-4">
        <Empty className="border border-dashed border-line-2">
          <EmptyHeader>
            <EmptyTitle>Firm admins review proposed companies</EmptyTitle>
            <EmptyDescription>Ask an admin to review them.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const toggle = (id: string) =>
    setState((s) => {
      const approved = new Set(s.approved);
      if (approved.has(id)) approved.delete(id);
      else approved.add(id);
      return { ...s, approved };
    });

  const apply = async () => {
    setBusy(true);
    try {
      const out = await CompanyMigrationAPI.apply(decisions);
      const linked = out.reduce((n, r) => n + (r.linked?.length ?? 0), 0);
      toast.success(
        `Linked ${linked} ${linked === 1 ? "case" : "cases"} to ${out.length} ${
          out.length === 1 ? "company" : "companies"
        }`
      );
      setState(emptyReview());
      await refreshCompanies();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't apply the review"
      );
      void refreshCompanies();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface"
      data-testid="migration-review"
    >
      <div className="flex h-13 shrink-0 items-center gap-2.5 px-4">
        <Button
          asChild
          size="icon-sm"
          variant="ghost"
          aria-label="Back to Companies"
        >
          <Link to={COMPANIES_PATH}>
            <ArrowLeft />
          </Link>
        </Button>
        <span className="text-base font-medium tracking-tight">
          Companies from existing cases
        </span>
        <span className="flex-1" />
        {proposed.length ? (
          <Button
            variant="primary"
            size="sm"
            disabled={!decisions.length || busy}
            onClick={() => void apply()}
          >
            {!decisions.length
              ? "Link companies"
              : decisions.length === 1
              ? "Link 1 company"
              : `Link ${decisions.length} companies`}
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pb-6">
        <p className="text-base text-fg-3">
          Cases with the same employer name are grouped, split by address when
          one name appears at two. Approve the ones to create; each case keeps
          its employer details until the company is next edited.
          {proposals.data?.skipped.no_employer === 1
            ? " 1 case has no employer and is left out."
            : proposals.data?.skipped.no_employer
            ? ` ${proposals.data.skipped.no_employer} cases have no employer and are left out.`
            : ""}
        </p>

        {near.length ? (
          <div className="flex flex-col gap-2" data-testid="near-matches">
            <span className="text-sm font-medium text-fg-3">
              Might be the same employer
            </span>
            {near.map((n) => (
              <div
                key={n.group_ids.join("-")}
                className="flex items-center gap-3 rounded-lg border border-line-2 bg-warn-soft px-3 py-2"
              >
                <span className="min-w-0 flex-1 text-base">
                  <span className="font-medium">{n.names[0]}</span> and{" "}
                  <span className="font-medium">{n.names[1]}</span>
                  <span className="text-fg-3"> · {n.reason}</span>
                </span>
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() =>
                    setState((s) => merge(s, n.group_ids[0], n.group_ids[1]))
                  }
                >
                  <GitMerge /> Merge
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        {!proposed.length ? (
          <Empty className="border border-dashed border-line-2">
            <EmptyHeader>
              <EmptyTitle>Nothing to review</EmptyTitle>
              <EmptyDescription>
                Every case with an employer is already linked to a company.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          proposed.map((p) => (
            <div
              key={p.root.id}
              className="rounded-lg border border-line-2"
              data-testid={`proposal-${p.root.id}`}
            >
              <div className="flex items-center gap-3 border-b border-line-2 px-3.5 py-2.5">
                <Checkbox
                  checked={state.approved.has(p.root.id)}
                  onCheckedChange={() => toggle(p.root.id)}
                  aria-label={`Approve ${p.root.name}`}
                />
                {p.existingCompanyId ? (
                  <span className="flex-1 text-base">
                    Join{" "}
                    <span className="font-medium">
                      {existingName(p.existingCompanyId)}
                    </span>
                  </span>
                ) : (
                  <Input
                    aria-label="Company name"
                    className="h-8 max-w-xs text-base"
                    value={state.names[p.root.id] ?? p.root.name}
                    onChange={(e) => {
                      const { value } = e.currentTarget;
                      setState((s) => ({
                        ...s,
                        names: { ...s.names, [p.root.id]: value }
                      }));
                    }}
                  />
                )}
                <span className="flex-1" />
                <Badge variant="secondary">
                  {p.caseCount} {p.caseCount === 1 ? "case" : "cases"}
                </Badge>
              </div>
              {p.groups.map((g, i) => (
                <div
                  key={g.id}
                  className="flex gap-3 border-b border-line-2 px-3.5 py-2.5 text-base last:border-b-0"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="font-medium">
                      {g.seed.legalBusinessName}
                    </span>
                    <span className="text-sm text-fg-3">
                      {[
                        formatAddress(g.seed.businessAddress) || "No address",
                        g.seed.contactName,
                        g.seed.contactEmail
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <span className="truncate text-sm text-fg-3">
                      {g.cases
                        .map((c) => (c.visa ? `${c.name} (${c.visa})` : c.name))
                        .join(", ")}
                    </span>
                  </span>
                  {i > 0 ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setState((s) => split(s, g.id))}
                    >
                      <Split /> Keep apart
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
